import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { ApplicationInsightsService, AppInsightTags } from '@app/core/services/application-insights.service';
import { ProviderService } from '@app/core/services/provider.service';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { PracticeAgentSuppressionMode } from './practice-agent-message.models';

const WIDGET_STATE_COLLECTION_ROOT = 'Experiments/PracticeAgentClinicalWidget/Practice';
const PRESENTATION_LEASE_DURATION_MS = 5 * 60 * 1000;

interface FirestoreWidgetState {
  SuppressionMode?: PracticeAgentSuppressionMode;
  SuppressionUntil?: firebase.firestore.Timestamp;
  PresentationLeaseId?: string;
  PresentationLeaseUntil?: firebase.firestore.Timestamp;
}

export interface PracticeAgentWidgetState {
  suppressionMode?: PracticeAgentSuppressionMode;
  suppressionUntil?: string;
}

export type PracticeAgentPresentationClaimResult = 'claimed' | 'blocked' | 'error';

interface PracticeAgentProviderIds {
  practiceTenantId: string;
  practiceId: string;
}

@Injectable({ providedIn: 'root' })
export class PracticeAgentWidgetStateService {
  private readonly providerIds$ = this.providerService.provider$.pipe(
    map(provider => ({
      practiceTenantId: provider?.PracticeTenantId || '',
      practiceId: provider?.PracticeId || '',
    })),
    filter(ids => !!ids.practiceTenantId && !!ids.practiceId),
    distinctUntilChanged(
      (previous, current) => previous.practiceTenantId === current.practiceTenantId && previous.practiceId === current.practiceId
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly state$ = this.providerIds$.pipe(
    switchMap(ids =>
      this.firestore
        .doc<FirestoreWidgetState>(this.getStatePath(ids))
        .valueChanges()
        .pipe(
          map(state => this.toWidgetState(state)),
          catchError(() => {
            this.trackFailure('read');
            return EMPTY;
          })
        )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private firestore: AngularFirestore,
    private providerService: ProviderService,
    private appInsightsService: ApplicationInsightsService
  ) {}

  saveSuppression(mode: PracticeAgentSuppressionMode, until: string): Observable<void> {
    return this.withProviderIds(ids =>
      from(
        this.firestore.doc(this.getStatePath(ids)).set(
          {
            SuppressionMode: mode,
            SuppressionUntil: firebase.firestore.Timestamp.fromDate(new Date(until)),
            PresentationLeaseId: firebase.firestore.FieldValue.delete(),
            PresentationLeaseUntil: firebase.firestore.FieldValue.delete(),
            UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        )
      ).pipe(map(() => undefined))
    );
  }

  clearSuppression(): Observable<void> {
    return this.withProviderIds(ids =>
      from(
        this.firestore.doc(this.getStatePath(ids)).set(
          {
            SuppressionMode: firebase.firestore.FieldValue.delete(),
            SuppressionUntil: firebase.firestore.FieldValue.delete(),
            UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        )
      ).pipe(map(() => undefined))
    );
  }

  claimPresentation(sessionId: string): Observable<PracticeAgentPresentationClaimResult> {
    return this.providerIds$.pipe(
      take(1),
      switchMap(ids =>
        from(
          this.firestore.firestore.runTransaction(async transaction => {
            const stateReference = this.firestore.firestore.doc(this.getStatePath(ids));
            const snapshot = await transaction.get(stateReference);
            const state = (snapshot.data() || {}) as FirestoreWidgetState;
            const nowMs = Date.now();
            const suppressionUntilMs = this.toMillis(state.SuppressionUntil);
            const leaseUntilMs = this.toMillis(state.PresentationLeaseUntil);

            if (suppressionUntilMs > nowMs) {
              return 'blocked' as PracticeAgentPresentationClaimResult;
            }

            if (state.PresentationLeaseId && state.PresentationLeaseId !== sessionId && leaseUntilMs > nowMs) {
              return 'blocked' as PracticeAgentPresentationClaimResult;
            }

            transaction.set(
              stateReference,
              {
                PresentationLeaseId: sessionId,
                PresentationLeaseUntil: firebase.firestore.Timestamp.fromMillis(nowMs + PRESENTATION_LEASE_DURATION_MS),
                UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
              },
              { merge: true }
            );
            return 'claimed' as PracticeAgentPresentationClaimResult;
          })
        )
      ),
      catchError(() => {
        this.trackFailure('claim');
        return of('error' as PracticeAgentPresentationClaimResult);
      })
    );
  }

  releasePresentation(sessionId: string): Observable<void> {
    return this.providerIds$.pipe(
      take(1),
      switchMap(ids =>
        from(
          this.firestore.firestore.runTransaction(async transaction => {
            const stateReference = this.firestore.firestore.doc(this.getStatePath(ids));
            const snapshot = await transaction.get(stateReference);
            const state = (snapshot.data() || {}) as FirestoreWidgetState;

            if (state.PresentationLeaseId !== sessionId) {
              return;
            }

            transaction.set(
              stateReference,
              {
                PresentationLeaseId: firebase.firestore.FieldValue.delete(),
                PresentationLeaseUntil: firebase.firestore.FieldValue.delete(),
                UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
              },
              { merge: true }
            );
          })
        ).pipe(map(() => undefined))
      ),
      catchError(() => {
        this.trackFailure('release');
        return EMPTY;
      })
    );
  }

  private withProviderIds(work: (ids: PracticeAgentProviderIds) => Observable<void>): Observable<void> {
    return this.providerIds$.pipe(
      take(1),
      switchMap(work),
      catchError(() => {
        this.trackFailure('write');
        return EMPTY;
      })
    );
  }

  private toWidgetState(state: FirestoreWidgetState | undefined): PracticeAgentWidgetState {
    const suppressionUntilMs = this.toMillis(state?.SuppressionUntil);
    const hasValidSuppression =
      (state?.SuppressionMode === 'dismissed' || state?.SuppressionMode === 'snoozed') && suppressionUntilMs > Date.now();

    if (!hasValidSuppression) {
      return {};
    }

    return {
      suppressionMode: state.SuppressionMode,
      suppressionUntil: new Date(suppressionUntilMs).toISOString(),
    };
  }

  private toMillis(value: firebase.firestore.Timestamp | undefined): number {
    return value && typeof value.toMillis === 'function' ? value.toMillis() : 0;
  }

  private getStatePath(ids: PracticeAgentProviderIds): string {
    return `${WIDGET_STATE_COLLECTION_ROOT}/${ids.practiceTenantId}/WidgetState/${ids.practiceId}`;
  }

  private trackFailure(operation: string): void {
    this.appInsightsService.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_STATE_FAILED, { operation });
  }
}
